import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money } from "../lib/api";
import SearchableSelect from "../components/ui/SearchableSelect";
import { todayIST, fmt12, fmtDate } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

const todayStr = todayIST;

export default function DailyStockOut() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skus, setSkus] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [skuId, setSkuId] = useState("");
  const [qty, setQty] = useState(1);
  const [issuedTo, setIssuedTo] = useState("");
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  // Owner request (20 Oct 2026): the Manual Out form carries the date itself.
  // Today records a normal movement; a past date records a late entry. Stock
  // is deducted now in both cases.
  const [outDate, setOutDate] = useState(todayStr());
  // Day-wise view. The owner reconciles Stock History day by day, so this list
  // must be able to show any single day -- not only today -- and must total
  // exactly what Stock History shows for that day.
  const [viewDate, setViewDate] = useState(todayStr());

  // Silent background refresh: the 30s poll and the refocus pull must not
  // touch `loading`, or the list collapses into a skeleton every 30 seconds
  // right in front of the user. A failed background poll also stays quiet —
  // only an explicit (user-initiated) load is allowed to surface an error.
  const fetchDay = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const t = viewDate;
      const res = await api.get(`/inventory/ledger?date_from=${t}&date_to=${t}&limit=100000`);
      // Out = negative movements, except purchase-invoice corrections: those
      // are signed In adjustments (a -7 invoice edit reduces In; it is not
      // stock leaving the shelf). Same rule as Stock History, so this day's
      // total now matches that day's Out there.
      setRows((res.data || []).filter((l) =>
        Number(l.quantity) < 0 &&
        (l.transaction_type || "").trim().toLowerCase() !== "purchase_invoice"));
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDay();
  }, [viewDate]);

  useEffect(() => {
    api.get("/inventory/skus").then((r) => setSkus(r.data || [])).catch(() => {});
  }, []);
  // Poll + refocus through the shared hook, which coalesces the focus and
  // visibilitychange pair that fire together on a tab switch into one reload.
  useAutoRefresh(() => fetchDay(true), 30000);

  // Owner rule (20 Oct 2026): never allow a manual stock out at zero stock.
  // Zero-stock products are not offered at all, and a selected product whose
  // stock dropped to zero since the page loaded is refused before the API call
  // (the backend enforces the same rule).
  const outSkus = skus.filter((s) => Number(s.store_qty) > 0);

  const handleManualOut = async (e) => {
    e.preventDefault();
    if (!skuId) return toast.error("Select a product");
    const selected = skus.find((s) => s.id === skuId);
    if (selected && Number(selected.store_qty) <= 0) {
      return toast.error(`${selected.name} is out of stock — stock out not allowed`);
    }
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error("Enter a valid quantity");
    if (selected && q > Number(selected.store_qty)) {
      return toast.error(`Only ${Number(selected.store_qty)} ${selected.unit || ""} available in stock`);
    }
    setSaving(true);
    try {
      const res = await api.post("/inventory/stock-out/manual-log", {
        sku_id: skuId, quantity: q, issued_to: issuedTo, reason, remarks,
        date: outDate,
      });
      toast.success(res.data.message || "Manual out recorded (inventory untouched).");
      setShowModal(false);
      setSkuId(""); setQty(1); setIssuedTo(""); setReason(""); setRemarks("");
      setOutDate(todayStr());
      // Show the day the row landed on, so a late entry is visible immediately.
      if (outDate !== viewDate) setViewDate(outDate);
      fetchDay(true); // in-place: the new row appears without a skeleton flash
      // Refresh the picker too, so a product that just hit zero disappears.
      api.get("/inventory/skus").then((r) => setSkus(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const totalUnits = rows.reduce((t, l) => t + Math.abs(Number(l.quantity) || 0), 0);

  return (
    <div className="lss-card p-6 sm:p-8 space-y-6 min-h-[70vh]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="lss-overline text-amber-800">Outward Flow</span>
          <h2 className="font-serif-lux text-3xl sm:text-4xl font-bold text-slate-950 mt-1">Daily Stock Out Items</h2>
          <p className="text-slate-600 text-sm font-medium mt-1">
            Every stock movement out on {fmtDate(viewDate)} · {rows.length} lines · {totalUnits.toLocaleString()} units out
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="date"
            max={todayStr()}
            value={viewDate}
            onChange={(e) => setViewDate(e.target.value || todayStr())}
            className="lss-input text-xs font-bold"
            title="Pick any day to see that day's stock out"
          />
          <button
            onClick={() => {
              if (!rows.length) return toast.error("No rows to export");
              const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
              const csv = [
                ["Date", "Time", "Product", "Type", "Qty Out", "By", "Remarks"].join(","),
                ...rows.map((l) => [fmtDate(l.date), fmt12(l.time), l.product_name, (l.transaction_type || "").replace(/_/g, " "), Math.abs(Number(l.quantity) || 0), l.performed_by, l.remarks].map(q).join(",")),
              ].join("\n");
              const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `Daily_Stock_Out_${viewDate}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success(`Exported ${rows.length} stock-out lines!`);
            }}
            className="px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 transition-all"
          >
            <Download className="w-4 h-4" /> Export
          </button>
          <button
            onClick={() => { setOutDate(todayStr()); setShowModal(true); }}
            className="lss-btn-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Manual Out
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-10 bg-slate-200 rounded animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm font-medium border border-dashed border-slate-300 rounded-xl">
          No stock-out movements on {fmtDate(viewDate)}.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="lss-table">
            <thead>
              <tr className="bg-slate-950 text-amber-300">
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">Time</th>
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">Date</th>
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">Product</th>
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">Type</th>
                <th className="text-right py-3 px-4 text-xs font-extrabold uppercase">Qty Out</th>
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">By</th>
                <th className="text-left py-3 px-4 text-xs font-extrabold uppercase">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50/80">
                  <td className="text-sm font-mono font-bold text-slate-700 px-4 py-3">{fmt12(l.time) || "—"}</td>
                  <td className="text-sm font-mono font-bold text-slate-700 px-4 py-3">{fmtDate(l.date) || "—"}</td>
                  <td className="text-sm font-bold text-slate-950 px-4 py-3">{l.product_name}</td>
                  <td className="text-xs"><span className="lss-badge text-[10px] bg-slate-100 text-slate-800">{(l.transaction_type || "").replace(/_/g, " ")}</span></td>
                  <td className="text-right tabular font-extrabold text-base text-rose-700 px-4 py-3">-{Math.abs(Number(l.quantity) || 0)}</td>
                  <td className="text-sm text-slate-600 px-4 py-3">{l.performed_by || "—"}</td>
                  <td className="text-xs text-slate-600 whitespace-normal break-words min-w-[16rem]" title={l.remarks}>{l.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && createPortal(
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-[100] overflow-y-auto" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-950 text-white px-6 py-4">
              <div className="lss-overline text-amber-300">Deducts from inventory</div>
              <h3 className="font-serif-lux text-2xl sm:text-3xl font-bold mt-0.5">Manual Stock Out</h3>
            </div>
            <form onSubmit={handleManualOut} className="p-6 sm:p-8 space-y-5 text-base">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Product *</label>
                <SearchableSelect
                  options={outSkus}
                  value={skuId}
                  onChange={(val) => setSkuId(val)}
                  placeholder="Search product with stock..."
                  getOptionLabel={(s) => s.name}
                  getOptionValue={(s) => s.id}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    max={todayStr()}
                    value={outDate}
                    onChange={(e) => setOutDate(e.target.value)}
                    className="lss-input w-full text-sm font-bold"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Quantity *</label>
                  <input type="number" min="0.01" step="any" required value={qty} onChange={(e) => setQty(e.target.value)} className="lss-input w-full font-bold tabular" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Issued To</label>
                  <input type="text" value={issuedTo} onChange={(e) => setIssuedTo(e.target.value)} placeholder="e.g. Floor Stylists" className="lss-input w-full text-xs font-semibold" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Reason</label>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. demo, damage, transfer" className="lss-input w-full text-xs font-semibold" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Remarks</label>
                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional notes..." className="lss-input w-full text-xs font-semibold h-16" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="lss-btn-outline px-4 py-2.5 text-xs font-bold uppercase tracking-wider">Cancel</button>
                <button type="submit" disabled={saving} className="lss-btn-gold px-6 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                  {saving ? "Saving..." : "Record Out"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
